import startDirectionsMarkdown from "../../../../lib/site_pages/agent-directions/start.md?raw"
import catalogJson from "./generated/catalog.json"
import frameworksJson from "./generated/frameworks.json"
import platformsJson from "./generated/platforms.json"
import landingCopyJson from "../../../../config/landing_copy.json"
import type {
  AgentDirections,
  AgentDirectionsMeta,
  AgentStack,
  ApiEntry,
  ApiPlatform,
  DocsCatalog,
  Framework,
  Platform,
  PlatformKind,
  PlatformsIndex,
  TestedRun
} from "./types"
import type { LandingCopy } from "./landing"

// Only the small contract files are imported here. The guides, the API
// reference, the search index and the agent directions' text load on demand
// (./lazy), so they stay out of every landing page's bundle.
export const CATALOG = catalogJson as DocsCatalog

// Each payload's metadata — paths, title, size — without its text, which
// ./lazy fetches when a copy button needs it.
export const AGENT_DIRECTIONS: AgentDirectionsMeta[] = CATALOG.agentDirections

export const API_PLATFORM_FILTERS: { id: "all" | ApiPlatform; label: string }[] = [
  { id: "all", label: "All platforms" },
  { id: "http", label: "HTTP" },
  { id: "node", label: "Node.js" },
  { id: "rails", label: "Rails" },
  { id: "python", label: "Python" },
  { id: "php", label: "PHP" },
  { id: "browser", label: "Browser" },
  { id: "cli", label: "CLI" }
]

export const matchesApiPlatform = (
  entry: ApiEntry,
  platform: "all" | ApiPlatform
): boolean => {
  if (platform === "all") return true
  if (platform === "node") return entry.platform === "node" || entry.platform === "host"
  return entry.platform === platform
}

// The words of a search query worth highlighting in its results.
export const highlightTerms = (query: string): string[] =>
  query
    .trim()
    .split(/\s+/)
    .map((term) => term.replace(/[^\p{L}\p{N}_.-]/gu, ""))
    .filter((term) => term.length >= 2)

// The framework landing pages, in the contract's order — the homepage grid
// and the header menu. A framework the bundle does not carry has no page.
export const FRAMEWORKS = frameworksJson as Framework[]

export const frameworkById = (id: string | null | undefined): Framework | null =>
  FRAMEWORKS.find((framework) => framework.id === id) ?? null

// The framework whose page copies a given payload — "Express" for the node
// stack, and so on. The copy modal labels its rows from this.
export const frameworkForStack = (stack: AgentStack): Framework | null =>
  FRAMEWORKS.find((framework) => framework.agentStack === stack) ?? null

// The platform rows (contract v8), in the contract's order, and the /platforms
// index. A row with no path is planned: listed as coming next, never linked.
const PLATFORMS_DATA = platformsJson as { index: PlatformsIndex | null; rows: Platform[] }
export const PLATFORMS = PLATFORMS_DATA.rows
export const PLATFORMS_INDEX = PLATFORMS_DATA.index
export const LIVE_PLATFORMS = PLATFORMS.filter((platform) => platform.path)

// Only rows with a page: a planned id is not a page, whatever the URL says.
export const platformById = (id: string | null | undefined): Platform | null =>
  LIVE_PLATFORMS.find((platform) => platform.id === id) ?? null

// The H1 and <title> of /platforms/<id> — PlatformPage#heading on the server.
export const platformHeadline = (platform: Platform): string =>
  LANDING_COPY.platforms.pageHeadline.replace(/\{label\}/g, platform.label)

export const PLATFORM_KIND_ORDER: PlatformKind[] = ["ai-builder", "host", "database", "store"]

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

// "Tested live · Oct 7, 2026 · 0.4.19" — the spec's badge, the same words
// PlatformPage.tested_label puts in the server body. null without a run: the
// badge is a claim that three agent trials passed, never a default.
export const testedLabel = (tested: TestedRun | null | undefined): string | null => {
  const match = tested ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(tested.date) : null
  if (!tested || !match || !tested.release) return null
  const [, year, month, day] = match
  const date = `${MONTHS[Number(month) - 1]} ${Number(day)}, ${year}`
  return tested.via === "btcpay-route"
    ? `Tested via BTCPay · ${date} · plugin ${tested.release}`
    : `Tested live · ${date} · ${tested.release}`
}

// Hand-maintained marketing copy, read by the Rails side too for the
// server-rendered body, so the two cannot say different things.
export const LANDING_COPY = landingCopyJson as LandingCopy

// The H1 of /integrations/<id>: the copy row's own where the searches use
// other words than the contract's heading. FrameworkPage#heading on the server.
export const frameworkHeading = (framework: Framework): string =>
  LANDING_COPY.frameworks[framework.id]?.heading || framework.heading

// The <title> of /integrations/<id>: FrameworkPage#title on the server.
export const frameworkTitle = (framework: Framework): string =>
  `${LANDING_COPY.frameworks[framework.id]?.title || frameworkHeading(framework)} — OpenReceive`

// A framework quickstart's <title>, or null for any other guide (and for a
// recipe or a BTCPay-route store): FrameworkPage#quickstart_search_title.
export const quickstartTitle = (guidePath: string): string | null => {
  const framework = FRAMEWORKS.find((row) => row.quickstartPath === guidePath)
  if (!framework || framework.type === "recipe" || framework.type === "via-btcpay") return null
  return `${LANDING_COPY.integrations.quickstartTitle.replace(/\{label\}/g, framework.label)} — OpenReceive`
}

// The "Related" row at the foot of /integrations/<id>: the platform pages that
// run on this framework, then the framework pages the copy pairs it with, then
// the /learn articles it names, labelled from the bootstrap's learn cards (a
// /learn page is server-rendered, so it carries no id to route on).
// FrameworkPage#related_links on the server.
export const relatedLinks = (
  framework: Framework,
  learnPages: { path: string; title: string }[] = []
): { label: string; href: string; platformId?: string; frameworkId?: string }[] => {
  const paired = LANDING_COPY.integrations.related[framework.id] ?? []
  return [
    ...LIVE_PLATFORMS.filter((platform) => platform.frameworkPath === `/integrations/${framework.id}`).map(
      (platform) => ({ label: platform.label, href: platform.path as string, platformId: platform.id })
    ),
    ...paired
      .map((id) => frameworkById(id))
      .filter((row): row is Framework => row !== null)
      .map((row) => ({ label: row.label, href: `/integrations/${row.id}`, frameworkId: row.id })),
    ...paired.flatMap((path) => learnPages.filter((page) => page.path === path))
      .map((page) => ({ label: page.title, href: page.path }))
  ]
}

// The site's own "start here" directions, the home modal's default: they find
// the stack (or pick one for a project that does not exist yet) and send the
// agent to that stack's full.md. The same file Rails serves at `path`.
export const START_DIRECTIONS: AgentDirections = {
  stack: "start",
  path: "/agent-directions/start.md",
  coverPath: "/agent-directions/start.md",
  source: "lib/site_pages/agent-directions/start.md",
  contentType: "text/markdown; charset=utf-8",
  copyButton: true,
  selfContained: false,
  title: "Agent directions (start here)",
  guidePath: null,
  bytes: new TextEncoder().encode(startDirectionsMarkdown).length,
  markdown: startDirectionsMarkdown
}

const AGENT_DIRECTIONS_BY_STACK = Object.fromEntries(
  AGENT_DIRECTIONS.map((entry) => [entry.stack, entry])
) as Record<Exclude<AgentStack, "start">, AgentDirectionsMeta>

export const agentDirectionsFor = (stack: AgentStack): AgentDirectionsMeta =>
  stack === "start" ? START_DIRECTIONS : AGENT_DIRECTIONS_BY_STACK[stack]
